import React, { useState, useEffect } from 'react';
import { DialogTitle } from '@/components/ui/dialog';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useToast } from '@/components/ui/use-toast';
import { recordPremiumPayment } from '@/lib/actions/premiumActions';
import { useAuth } from '@/context/AuthContext';
import { Loader2, AlertCircle, Info } from 'lucide-react';
import { checkFreePremiumListingsAvailable, useFreePremiumListing, markListingAsPremium } from '@/lib/paypal';
import { Button } from '@/components/ui/button';
import { getPremiumListingsUsage } from '@/lib/actions/subscriptionActions';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { sendPremiumPurchaseEmail } from '@/lib/actions/emailActions';
import { PAYPAL_CLIENT_ID } from '@/lib/paypal';
import { isCapacitorApp } from '@/lib/utils'; // Neu importiert
import { Alert, AlertDescription, AlertTitle as UiAlertTitle } from '@/components/ui/alert'; // UiAlertTitle als Alias, um Konflikt mit DialogTitle zu vermeiden

interface PremiumPaymentProps {
  listingId: number;
  onSuccess: () => void;
  onCancel: () => void;
  onClose: () => void; 
}

const PremiumPayment = ({ listingId, onSuccess, onCancel, onClose }: PremiumPaymentProps) => {
  const { toast } = useToast();
  const { profile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState<string | null>(null);

  // Fetch premium listings usage data
  const { data: usageData, isLoading: isLoadingUsage } = useQuery({
    queryKey: ['premiumListingsUsage', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return null;
      return getPremiumListingsUsage(profile.id);
    },
    enabled: !!profile?.id,
  });

  // Track PayPal script loading
  useEffect(() => {
    const checkPayPalSDK = () => {
      const paypalScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
      if (paypalScript) {
        const handleLoad = () => setScriptLoaded(true);
        const handleError = () => {
          setScriptError('Error loading PayPal. Please try again later.');
          console.error('PayPal script failed to load');
        };
        
        paypalScript.addEventListener('load', handleLoad);
        paypalScript.addEventListener('error', handleError);
        
        return () => {
          paypalScript.removeEventListener('load', handleLoad);
          paypalScript.removeEventListener('error', handleError);
        };
      }
    };
    
    const cleanup = checkPayPalSDK();
    return cleanup;
  }, []);

  // Determine if user has premium listings available through subscription
  const hasFreeListings = usageData?.has_subscription && usageData?.premium_listings_available > 0;
  
  // Validate listing exists before proceeding with payment
  const { data: listing, isLoading: isValidatingListing } = useQuery({
    queryKey: ['listing', listingId],
    queryFn: async () => {
      if (!listingId) return null;
      const { data, error } = await supabase
        .from('listings')
        .select('id, title')
        .eq('id', listingId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!listingId
  });

  const runningInCapacitor = isCapacitorApp();

  const handlePayPalSuccess = async (details: any) => {
    if (!profile?.id || !listingId) {
      toast({
        title: "Fehler",
        description: "Fehlende Benutzer- oder Listungsinformationen",
        variant: "destructive"
      });
      return;
    }

    if (!listing) {
      toast({
        title: "Fehler",
        description: "Die ausgewählte Listung existiert nicht",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('Processing PayPal payment success', { details, listingId });
      
      // Use the database function to create premium payment and update listing
      // Use type assertion to work around TypeScript error
      const { data, error } = await (supabase.rpc as any)('create_premium_payment_and_update_listing', {
        p_listing_id: listingId,
        p_user_id: profile.id
      });

      if (error) throw error;
      
      // Send confirmation email
      try {
        const { data: userData } = await supabase
          .from('profiles')
          .select('email, username')
          .eq('id', profile.id)
          .single();
          
        if (userData?.email) {
          await sendPremiumPurchaseEmail(
            userData.email,
            userData.username || 'User',
            listing.title
          );
        }
      } catch (emailError) {
        console.error('Error sending premium purchase email:', emailError);
      }

      toast({
        title: "Premium aktiviert",
        description: "Ihre Listung ist jetzt Premium",
      });

      onSuccess();
    } catch (error: any) {
      console.error('Payment error:', error);
      toast({
        title: "Fehler bei der Bezahlung",
        description: error.message || "Ein Fehler ist aufgetreten",
        variant: "destructive"
      });
      setError("Ein Fehler ist während der Bezahlung aufgetreten");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseFreeSubscriptionListing = async () => {
    if (!profile?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      // Use free premium listing from subscription
      const success = await useFreePremiumListing(profile.id, listingId);
      
      if (success) {
        // Send confirmation email if successful
        try {
          const { data: userData } = await supabase
            .from('profiles')
            .select('email, username')
            .eq('id', profile.id)
            .single();
            
          const { data: listingData } = await supabase
            .from('listings')
            .select('title')
            .eq('id', listingId)
            .single();
            
          if (userData?.email && listingData) {
            await sendPremiumPurchaseEmail(
              userData.email,
              userData.username || 'User',
              listingData.title
            );
          }
        } catch (emailError) {
          console.error('Error sending premium purchase email:', emailError);
        }

        toast({
          title: 'Shpallja Premium aktivizuar',
          description: 'Shpallja juaj tani është Premium (nga abonimi juaj)',
        });

        onSuccess();
      } else {
        throw new Error('Failed to use free premium listing');
      }
    } catch (error) {
      toast({
        title: 'Gabim',
        description: 'Ndodhi një problem gjatë aktivizimit të shpalljes premium',
        variant: 'destructive'
      });
      setError('Ndodhi një problem gjatë aktivizimit të shpalljes premium');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogTitle className="sr-only">Aktivizo Shpalljen Premium</DialogTitle>
      
      <div className="p-6 space-y-4">
        <h2 className="text-xl font-bold text-center">Aktivizo Shpalljen Premium</h2>
        <p className="text-center text-muted-foreground">
          Përfitoni më shumë vizibilitet me shpalljen Premium
        </p>

        {error && (
          <div className="p-3 rounded bg-red-50 text-red-500 text-sm">
            {error}
          </div>
        )}

        {scriptError && (
          <div className="p-3 rounded bg-red-50 text-red-500 text-sm">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {scriptError}
            </div>
            <p className="mt-1 text-xs">Provoni ta rifreskoni faqen ose kontaktoni suportin nëse problemi vazhdon.</p>
          </div>
        )}

        {isLoadingUsage ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            <span className="ml-2">Duke kontrolluar abonimin tuaj...</span>
          </div>
        ) : hasFreeListings ? (
          <div className="space-y-4">
            <div className="p-4 rounded bg-green-50 text-green-600 text-sm">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-5 w-5" />
                <p className="font-medium">Ju keni shpallje Premium në abonimin tuaj!</p>
              </div>
              <p>Aktualisht keni <strong>{usageData?.premium_listings_available}</strong> shpallje Premium të mbetura nga <strong>{usageData?.premium_listings_total}</strong> në total.</p>
              <p className="mt-1">Mund ta aktivizoni statusin Premium pa pagesë shtesë.</p>
            </div>
            
            <Button 
              onClick={handleUseFreeSubscriptionListing}
              disabled={isLoading}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Duke aktivizuar...
                </>
              ) : (
                'Aktivizo Premium nga abonimi'
              )}
            </Button>
          </div>
        ) : runningInCapacitor ? (
          <Alert>
            <Info className="h-4 w-4" />
            <UiAlertTitle>PayPal Zahlung</UiAlertTitle>
            <AlertDescription>
              Um Ihren Premium-Kauf über PayPal abzuschließen, besuchen Sie bitte unsere Webseite in einem Standard-Webbrowser. 
              PayPal-Zahlungen innerhalb der App werden für ein optimales Erlebnis derzeit am besten über die Webseite abgewickelt.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {usageData?.has_subscription && (
              <div className="p-3 rounded bg-amber-50 text-amber-700 text-sm mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Info className="h-4 w-4" />
                  <p className="font-medium">Ju keni përdorur të gjitha shpalljet Premium nga abonimi juaj.</p>
                </div>
                <p>Me abonimin <strong>{usageData?.plan_id === 'pro' ? 'Pro' : 'Business'}</strong>, 
                  ju keni <strong>{usageData?.premium_listings_total}</strong> shpallje Premium në muaj, 
                  të cilat janë të përfshira në abonim.</p>
              </div>
            )}

            <PayPalScriptProvider 
              options={{ 
                clientId: PAYPAL_CLIENT_ID,
                currency: "EUR",
                intent: "capture",
                'enable-funding': 'card',
                'disable-funding': 'paylater,venmo',
                components: "buttons",
                locale: "en_US"
              }}
            >
              {isLoading || isValidatingListing ? ( // Added isValidatingListing to loading condition
                <div className="flex justify-center py-4">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  <p className="text-center text-sm mb-2">Paguaj për të aktivizuar shpalljen Premium</p>
                  <PayPalButtons
                    style={{ layout: "vertical" }}
                    disabled={!listing} // Disable if listing is not yet validated
                    createOrder={(data, actions) => {
                      return actions.order.create({
                        intent: "CAPTURE",
                        purchase_units: [{
                          amount: {
                            currency_code: "EUR",
                            value: "9.99" // TODO: Consider making this dynamic or configurable
                          },
                          description: `Premium listing upgrade: ${listing?.title || listingId}`
                        }],
                        application_context: {
                          shipping_preference: "NO_SHIPPING",
                          user_action: "PAY_NOW"
                        }
                      });
                    }}
                    onApprove={(data, actions) => {
                      return actions.order!.capture().then(handlePayPalSuccess);
                    }}
                    onCancel={(data) => {
                      console.log("Payment cancelled", data);
                      onCancel();
                    }}
                    onError={(err) => {
                      console.error("PayPal error", err);
                      setError("Gabim me PayPal: " + (err.message || "Ju lutemi provoni përsëri"));
                    }}
                  />
                </>
              )}
            </PayPalScriptProvider>
          </>
        )}
      </div>
    </>
  );
};

export default PremiumPayment;
